/**
 * 테이블 찾기 팝업 — 테이블명으로 검색하거나, 스키마를 골라 그 안의 목록에서 고른다.
 *
 * 예전에는 스키마를 먼저 고르고 그 스키마의 전체 목록에서 라디오로 골랐다. 테이블 이름은 알지만 어느
 * 스키마인지 모르는 경우가 흔하고, 스키마·테이블이 많은 DB 에서는 찾기 어려웠다(현장 요청 2026-09-17).
 *
 *  - 왼쪽 '전체' : 이름으로 모든 스키마를 검색 (서버 GET …/table-search/)
 *  - 왼쪽 스키마 : 그 스키마의 목록을 읽어 이름으로 거른다
 * 고르면 스키마와 테이블이 함께 정해진다. 크기는 고정 — 목록 길이에 따라 창이 들썩이지 않게.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../../store';
import { fetchSchemas, fetchTables, tableMapKey } from '../../../../store/connectionsSlice';
import { be1Fetch } from '../../../../config/api';
import { formatFetchedAt, smallBtn } from './ui';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';

export interface FoundTable { schema: string; name: string; comment?: string | null }

interface Props {
  connection: string;
  schema?: string;
  table?: string;
  onSelect: (t: FoundTable) => void;
  onClose: () => void;
}

const ALL = '__all__';

/** 이름 검색 — 스키마를 가리지 않는다. 화면 여러 곳(속성 칸 자동완성·이 팝업)이 같이 쓴다 */
export async function searchTables(connection: string, q: string, limit = 100): Promise<{ tables: FoundTable[]; truncated: boolean }> {
  const res = await be1Fetch(`/api/connections/${encodeURIComponent(connection)}/table-search/?q=${encodeURIComponent(q)}&limit=${limit}`,
    { credentials: 'include' });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(body, res.statusText));
  return { tables: body.tables || [], truncated: !!body.truncated };
}

const TableFinderModal: React.FC<Props> = ({ connection, schema, table, onSelect, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const schemas = useSelector((s: RootState) => s.connections.schemaMap[connection]);
  const tableMap = useSelector((s: RootState) => s.connections.tableMap);
  const fetchedAtMap = useSelector((s: RootState) => s.connections.tableFetchedAt);

  const [scope, setScope] = useState<string>(ALL);
  const [q, setQ] = useState(table || '');
  const [found, setFound] = useState<{ tables: FoundTable[]; truncated: boolean; at: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  useEffect(() => { if (connection && !schemas) dispatch(fetchSchemas(connection)); }, [connection, schemas, dispatch]);

  // 전체 검색 — 입력이 멈추면 서버에 묻는다
  const runSearch = React.useCallback(async (term: string) => {
    setBusy(true); setError('');
    try {
      const r = await searchTables(connection, term, 200);
      setFound({ ...r, at: new Date().toISOString() });
    } catch (e: any) {
      setError(e?.message || t('property:finder.searchFailed'));
    } finally { setBusy(false); }
  }, [connection, t]);
  useEffect(() => {
    if (scope !== ALL) return undefined;
    const timer = setTimeout(() => { runSearch(q.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [q, scope, runSearch]);

  // 스키마 목록 — 없으면 읽는다
  const key = scope !== ALL ? tableMapKey(connection, scope) : '';
  useEffect(() => {
    if (scope !== ALL && !tableMap[key]) dispatch(fetchTables({ connection_id: connection, schema: scope }));
  }, [scope, key, tableMap, connection, dispatch]);

  const rows: FoundTable[] = useMemo(() => {
    if (scope === ALL) return found?.tables || [];
    const term = q.trim().toUpperCase();
    return ((tableMap[key] as any[]) || [])
      .map((row: any) => ({ schema: scope, name: typeof row === 'string' ? row : row.name, comment: typeof row === 'string' ? '' : row.comment }))
      .filter((row) => !term || row.name.toUpperCase().includes(term));
  }, [scope, found, tableMap, key, q]);

  const fetchedAt = scope === ALL ? found?.at : fetchedAtMap[key];
  const refresh = () => {
    if (scope === ALL) runSearch(q.trim());
    else dispatch(fetchTables({ connection_id: connection, schema: scope }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[860px] max-w-[95vw] h-[72vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('property:finder.title')}</div>
            <div className="text-xs text-gray-500">{t('property:finder.subtitle', { connection })}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:finder.close')}>✕</button>
        </div>

        <div className="px-4 py-2 border-b border-gray-200">
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
            placeholder={scope === ALL ? t('property:finder.placeholderAll') : t('property:finder.placeholderSchema', { schema: scope })} />
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="w-48 border-r border-gray-200 overflow-auto text-xs">
            <button className={`w-full text-left px-3 py-1.5 ${scope === ALL ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
              onClick={() => setScope(ALL)}>{t('property:finder.allSchemas')}</button>
            <div className="px-3 pt-2 pb-1 text-[0.625rem] text-gray-400">{t('property:finder.schemas')}</div>
            {(schemas || []).map((s) => (
              <button key={s} className={`w-full text-left px-3 py-1 truncate ${scope === s ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                title={s} onClick={() => setScope(s)}>{s}{s === schema ? ' ·' : ''}</button>
            ))}
            {schemas && schemas.length === 0 && <div className="px-3 py-1 text-gray-400">{t('property:finder.noSchemas')}</div>}
          </div>

          <div className="flex-1 min-w-0 overflow-auto">
            {error ? <div className="p-4 text-sm text-red-600">{error}</div>
              : (scope === ALL ? busy && !found : !tableMap[key]) ? <div className="p-4 text-sm text-gray-500">{t('property:finder.reading')}</div>
                : rows.length === 0 ? <div className="p-4 text-sm text-gray-500">{t('property:finder.noMatch')}</div>
                  : (
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="px-3 py-1.5 w-40">{t('property:finder.colSchema')}</th>
                          <th className="px-3 py-1.5">{t('property:finder.colTable')}</th>
                          <th className="px-3 py-1.5">{t('property:finder.colComment')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const current = row.name === table && (row.schema || '') === (schema || '');
                          return (
                            <tr key={`${row.schema}.${row.name}`} onClick={() => onSelect(row)}
                              className={`border-b border-gray-100 cursor-pointer ${current ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                              <td className="px-3 py-1 text-gray-500 font-mono truncate max-w-[10rem]" title={row.schema}>{row.schema}</td>
                              <td className="px-3 py-1 text-gray-900 font-mono">{row.name}{current && <span className="ml-1 text-blue-600">{t('property:finder.selected')}</span>}</td>
                              <td className="px-3 py-1 text-gray-500 truncate max-w-[18rem]" title={row.comment || ''}>{row.comment || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
          </div>
        </div>

        <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between gap-2">
          <span>
            {t('property:finder.count', { n: rows.length })}{scope === ALL && found?.truncated ? t('property:finder.more') : ''}
            {fetchedAt && <>{t('property:finder.fetchedAt', { at: formatFetchedAt(fetchedAt) })}</>}
          </span>
          <div className="flex items-center gap-2">
            <button className={smallBtn} onClick={refresh} disabled={busy} title={t('property:finder.refreshTitle')}>{t('property:finder.refresh')}</button>
            <button className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('property:finder.close')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableFinderModal;
